import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Button, IconButton, Stack } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

const DEFAULT_POSITION = { vertical: 'top', horizontal: 'center' };
const DEFAULT_DURATION = 6000;

/**
 * The app's one toast (design.md §5.5): `showNotification(message, severity, options)`.
 *
 * options:
 *  - duration : ms before it hides itself (default 6000)
 *  - position : Snackbar anchorOrigin
 *  - action   : { label, onClick } - one button in the toast (Undo, Open); pressing it runs
 *               onClick and closes the toast. A click elsewhere never closes a toast, so an
 *               action stays reachable; Escape and the close button do.
 */
export const NotificationProvider = ({ children }) => {
  const [state, setState] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success' | 'info' | 'warning' | 'error'
    duration: DEFAULT_DURATION,
    position: DEFAULT_POSITION,
    action: null,
    key: 0,
  });

  const showNotification = useCallback((message, severity = 'info', options = {}) => {
    setState((prev) => ({
      ...prev,
      open: true,
      message,
      severity,
      duration: options.duration || DEFAULT_DURATION,
      position: options.position || DEFAULT_POSITION,
      action: options.action && options.action.label ? options.action : null,
      // A new key remounts the Snackbar, so a second toast starts its own timer.
      key: prev.key + 1,
    }));
  }, []);

  const hideNotification = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const runAction = useCallback(() => {
    const { action } = state;
    setState((prev) => ({ ...prev, open: false }));
    if (action && typeof action.onClick === 'function') action.onClick();
  }, [state]);

  // With an `action`, the Alert leaves out its own close icon, so the button and a close sit together.
  const alertAction = state.action ? (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Button color="inherit" size="small" onClick={runAction} sx={{ fontWeight: 700 }}>
        {state.action.label}
      </Button>
      <IconButton size="small" color="inherit" aria-label="Close" onClick={hideNotification}>
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Stack>
  ) : undefined;

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification }}>
      {children}
      <Snackbar
        key={state.key}
        open={state.open}
        autoHideDuration={state.duration}
        onClose={hideNotification}
        anchorOrigin={state.position}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={hideNotification}
          action={alertAction}
          severity={state.severity}
          sx={{ width: '100%', mt: 8 }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
